/**
 * 模块: 配置预设API服务
 * 职责: 对接v2.0配置驱动系统的API接口
 * 限制: <100行
 */

import api from './api';

// ============================================================================
// 类型定义
// ============================================================================

export interface PresetListItem {
  key: string;
  name: string;
  description: string;
}

export interface PresetDetail {
  preset_key: string;
  name: string;
  description: string;
  config: WeightSystemConfig;
}

export interface WeightSystemConfig {
  version: string;
  name?: string;
  description?: string;
  market_opportunity: MetricConfig;
  entry_barrier: MetricConfig;
  traffic_quality: MetricConfig;
  ad_priority: MetricConfig;
  market_matrix: MatrixConfig;
}

export interface MetricConfig {
  method: string;
  components: ComponentConfig[];
  grade_thresholds?: Record<string, number>;
  strategy_mapping?: Record<string, string>;
}

export interface ComponentConfig {
  name: string;
  weight: number;
  normalization?: NormalizationConfig;
}

export interface NormalizationConfig {
  type: string;
  direction?: string;
  segments?: SegmentConfig[];
  [key: string]: any;
}

export interface SegmentConfig {
  min: number;
  max: number;
  score: number;
}

export interface MatrixConfig {
  opportunity_thresholds: { high: number; low: number };
  stability_thresholds: { high: number; low: number };
  types: Record<string, { opportunity: string; stability: string }>;
}

export interface ValidationResult {
  is_valid: boolean;
  errors: string[];
}

export interface WeightsSummary {
  presets: Record<string, PresetWeightsSummary>;
}

export interface PresetWeightsSummary {
  name: string;
  description: string;
  weights: Record<string, Record<string, number>>;
}

// ============================================================================
// API接口
// ============================================================================

export const configPresetAPI = {
  /**
   * 获取所有预设列表
   */
  listPresets: async (): Promise<{ presets: PresetListItem[]; count: number }> => {
    const response = await api.get('/config-presets/list/');
    return response.data;
  },

  /**
   * 获取指定预设的完整配置
   */
  getPreset: async (presetKey: string): Promise<PresetDetail> => {
    const response = await api.get(`/config-presets/get/${presetKey}/`);
    return response.data;
  },

  /**
   * 获取默认配置（均衡型）
   */
  getDefaultConfig: async (): Promise<{ preset_key: string; config: WeightSystemConfig }> => {
    const response = await api.get('/config-presets/default/');
    return response.data;
  },

  /**
   * 验证自定义配置
   */
  validateConfig: async (config: WeightSystemConfig): Promise<ValidationResult> => {
    const response = await api.post('/config-presets/validate/', { config });
    return response.data;
  },

  /**
   * 对比多个预设
   */
  comparePresets: async (presetKeys: string[]): Promise<{ comparison: Record<string, PresetDetail>; errors: string[] }> => {
    const response = await api.get('/config-presets/compare/', {
      params: { presets: presetKeys.join(',') },
    });
    return response.data;
  },

  /**
   * 获取所有预设的权重摘要（用于快速对比）
   */
  getWeightsSummary: async (): Promise<WeightsSummary> => {
    const response = await api.get('/config-presets/weights-summary/');
    return response.data;
  },
};

